/**
 * Privacy-friendly public display name helpers (mirrors backend/lib/displayName.js).
 * Options are derived from legal name only — never expose the full legal name publicly.
 */

export function tokenizeName(name) {
  return String(name || "")
    .normalize("NFKC")
    .trim()
    .split(/\s+/)
    .map((part) => {
      const cleaned = part.replace(/^[,]+|[,]+$/g, "").trim();
      if (/^[\p{L}]\.?$/u.test(cleaned)) {
        return cleaned.replace(/\.$/, "") + ".";
      }
      return cleaned.replace(/^[,.]+|[,.]+$/g, "").trim();
    })
    .filter(Boolean);
}

function initialOf(token) {
  const ch = String(token || "").match(/\p{L}/u);
  return ch ? `${ch[0].toUpperCase()}.` : "";
}

/**
 * Build abbreviated public name options from a legal name.
 * e.g. "Vikrant Prabhu Desai" → { full_initials: "Vikrant P. D.", privacy: "Prabhu D." }
 * @returns {{ full_initials: string, privacy: string, preferred_base: string }}
 */
export function generateDisplayNameOptions(legalName, _preferredName) {
  const legalParts = tokenizeName(legalName);
  if (!legalParts.length) {
    return { full_initials: "", privacy: "", preferred_base: "" };
  }

  const first = legalParts[0];
  const rest = legalParts.slice(1);

  // First name + initials of remaining parts: "Vikrant P. D."
  const full_initials = rest.length
    ? [first, ...rest.map(initialOf)].filter(Boolean).join(" ")
    : first;

  let privacy = "";
  if (legalParts.length >= 3) {
    // Middle name + last initial: "Prabhu D."
    const middle = legalParts[1];
    privacy = [middle, initialOf(legalParts[legalParts.length - 1])].filter(Boolean).join(" ");
  } else if (legalParts.length === 2) {
    // First initial + last name: "V. Desai"
    privacy = [initialOf(first), legalParts[1]].filter(Boolean).join(" ");
  } else {
    privacy = first;
  }

  // Deduplicate when both collapse to the same string
  if (privacy === full_initials && legalParts.length >= 2) {
    privacy = [initialOf(first), legalParts[legalParts.length - 1]].filter(Boolean).join(" ");
  }

  return {
    full_initials,
    privacy: privacy || full_initials,
    preferred_base: first,
  };
}

/**
 * Resolve the public display_name for storage.
 * full_legal is rejected — never return the full legal name as a public style.
 */
export function resolvePublicDisplayName({
  legalName,
  preferredName,
  nameStyle = "full_initials",
  customDisplayName = "",
}) {
  const options = generateDisplayNameOptions(legalName, preferredName);
  const style = nameStyle === "full_legal" ? "full_initials" : nameStyle;

  if (style === "custom") {
    const custom = String(customDisplayName || "").trim().replace(/\s+/g, " ");
    return custom || options.full_initials || options.preferred_base;
  }
  if (style === "privacy") {
    return options.privacy || options.full_initials || options.preferred_base;
  }
  return options.full_initials || options.privacy || options.preferred_base;
}
